import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildPublicPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const AboutSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Generate missing colors if they don't exist
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()
  const bgMediumColor = themeColors.bgMedium || "#E6DAD7"

  const layout = settings.layout || "image_left"
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Learn more"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled
    if (settings.showBackgroundColor && settings.backgroundColor === "theme") {
      // Use alert yellow if bgLight is missing to surface misconfiguration
      return themeColors.bgLight || "#FFF200"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  // Use fixed text color for all sections
  const headingColor = theme?.colors?.textDark || "#000000"
  const bodyColor = theme?.colors?.textDark || "#000000"

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex flex-col gap-12">
      {/* Text Content */}
      <div className="flex flex-col gap-6">
        {section.headline && (
          <h2
            className="text-[40px] font-bold leading-[48px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.body && (
          <>
            <style>{`
              .about-section-content {
                text-align: initial !important;
              }
              .about-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .about-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .about-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .about-section-content .ql-align-right {
                text-align: right !important;
              }
              .about-section-content .ql-align-center {
                text-align: center !important;
              }
              .about-section-content .ql-align-justify {
                text-align: justify !important;
              }
              .about-section-content .ql-align-left {
                text-align: left !important;
              }
              .about-section-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .about-section-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .about-section-content p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              .about-section-content p:first-child {
                margin-top: 0 !important;
              }
              .about-section-content p:last-child {
                margin-bottom: 0 !important;
              }
              .about-section-content strong,
              .about-section-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .about-section-content em,
              .about-section-content i {
                font-style: italic !important;
              }
              .about-section-content u {
                text-decoration: underline !important;
              }
              .about-section-content h1 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 56px !important;
                line-height: 72px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${headingColor} !important;
              }
              .about-section-content h2 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${headingColor} !important;
              }
              .about-section-content h3 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 27px !important;
                line-height: 40px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${headingColor} !important;
              }
              .about-section-content h4 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 20px !important;
                line-height: 32px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${headingColor} !important;
              }
              .about-section-content h5 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${headingColor} !important;
              }
              .about-section-content h6 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${headingColor} !important;
              }
              @media (max-width: 768px) {
                .about-section-content h1 {
                  font-size: 32px !important;
                  line-height: 48px !important;
                }
                .about-section-content h2 {
                  font-size: 27px !important;
                  line-height: 32px !important;
                }
                .about-section-content h3 {
                  font-size: 22px !important;
                  line-height: 32px !important;
                }
                .about-section-content h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                .about-section-content h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                .about-section-content h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
            <div
              className="about-section-content prose prose-lg max-w-none text-[20px] leading-[32px]" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={handleCTAClick}
          className={twMerge(
            "inline-flex w-fit items-center justify-center px-10 py-3 text-base font-normal leading-[24px] text-white transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {ctaText}
        </button>
      )}
    </div>
  )

  // Desktop-specific layouts with complex grid system
  switch (layout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex flex-row items-center gap-[104px]">
              {section.imageUrl ? (
                <div className="relative w-auto shrink-0">
                  {/* Desktop: Complex grid layout */}
                  <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-[0]">
                    <div
                      className={twMerge("[grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        marginLeft: "272px",
                        marginTop: "346px"
                      }}
                    />
                    <div
                      className={twMerge("bg-cover bg-center [grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        width: "464px",
                        height: "568px",
                        backgroundImage: `url(${section.imageUrl})`,
                        backgroundColor: bgMediumColor
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative w-auto shrink-0">
                  {/* Desktop: Complex layout */}
                  <div className="relative" style={{ width: "464px", height: "568px" }}>
                    <div
                      className="absolute"
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        left: "272px",
                        top: "346px"
                      }}
                    />
                    <div className="relative flex h-full w-full items-center justify-center bg-gray-200">
                      <span className="text-gray-500">No image selected</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent()}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex flex-row items-center gap-[104px]">
              {section.imageUrl ? (
                <div className="relative order-2 w-auto shrink-0">
                  {/* Desktop: Complex grid layout */}
                  <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-[0]">
                    <div
                      className={twMerge("[grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        marginLeft: "272px",
                        marginTop: "346px"
                      }}
                    />
                    <div
                      className={twMerge("bg-cover bg-center [grid-area:1_/_1]", getThemeShapeClasses(theme))}
                      style={{
                        width: "464px",
                        height: "568px",
                        backgroundImage: `url(${section.imageUrl})`,
                        backgroundColor: bgMediumColor
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative order-2 w-auto shrink-0">
                  {/* Desktop: Complex layout */}
                  <div className="relative" style={{ width: "464px", height: "568px" }}>
                    <div
                      className="absolute"
                      style={{
                        backgroundColor: bgMediumColor,
                        width: "240px",
                        height: "270px",
                        left: "272px",
                        top: "346px"
                      }}
                    />
                    <div className="relative flex h-full w-full items-center justify-center bg-gray-200">
                      <span className="text-gray-500">No image selected</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="order-1 w-full flex-1">{renderContent()}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-14",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto max-w-4xl px-16 text-center">
            {section.imageUrl && (
              <div className="mb-8 flex justify-center">
                <div className="relative">
                  <div
                    className={twMerge("absolute -bottom-6 -right-6 h-24 w-20", getThemeShapeClasses(theme))}
                    style={{ backgroundColor: theme?.colors?.accent || "#E6DAD7" }}
                  />
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "About image"}
                    className={twMerge("relative h-64 w-64 object-cover", getThemeShapeClasses(theme))}
                  />
                </div>
              </div>
            )}
            {renderContent()}
          </AnimatedSection>
        </section>
      )

    case "no_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-14",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto max-w-4xl px-16">{renderContent()}</AnimatedSection>
        </section>
      )
  }
}

export default AboutSectionDesktop
